import { useMemo } from 'react'
import { useAppStore } from '@forwardflow/store/appStore'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   useGetAllLeadSources,
   useGetAllRecommendedCampaign,
} from '@forwardflow/features/campaign'
import { useGetFubStages, useGetPeopleTags } from '@forwardflow/queries/onboarding.queries'
import { useFubPonds } from '@forwardflow/queries/fub.queries'
import { IAllLeadSources } from '@forwardflow/types'
import {
   DEMO_LEAD_SOURCES,
   DEMO_PONDS,
   DEMO_STAGES,
   DEMO_TAGS,
   isFreeFlow,
   isSandbox,
} from '../dev/devTools'
import {
   buildStarterCampaigns,
   IPondLike,
   IRecommendedLike,
   ISourceLike,
   IStarterCampaign,
   IVocabulary,
} from '../steps/first-campaign/starter-campaigns'

/**
 * Everything a campaign can be filtered by, from Follow Up Boss: sources
 * (with lead counts), stages, tags and ponds, plus the recommended
 * campaigns. Works on the dashboard and in the wizard; in the sandbox it
 * is the demo account.
 */
export const useCampaignVocabulary = () => {
   const { currentOrganization } = useAppStore()
   const {
      onboardingData,
      stages: storeStages,
      tags: storeTags,
      ponds: storePonds,
      sources: storeSources,
   } = useOnboardingStoreV2()
   const freeFlow = isFreeFlow()
   const orgId = isSandbox() ? '' : onboardingData.organizationId || currentOrganization || ''

   const { data: leadSources, isLoading: sourcesLoading } = useGetAllLeadSources({
      page: 1,
      limit: 50,
      search: '',
   })
   const { data: recommendedData } = useGetAllRecommendedCampaign()
   const { data: fubStages } = useGetFubStages(orgId, 10000, 1, true)
   const { data: fubTags } = useGetPeopleTags(orgId, 1, 10000, true)
   const { data: pondsData } = useFubPonds(orgId, 1, 10000, Boolean(orgId))

   const sources: ISourceLike[] = useMemo(() => {
      const rows = ((leadSources || []) as IAllLeadSources[]).filter((s) => s.leadSource)
      if (rows.length) return rows
      const fromStore = (storeSources || []).filter(Boolean).map((leadSource) => ({ leadSource }))
      if (fromStore.length) return fromStore
      return freeFlow ? DEMO_LEAD_SOURCES : []
   }, [leadSources, storeSources, freeFlow])

   const stages = useMemo(() => {
      const loaded =
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ id: String(s.stageId), name: s.stageName })) || []
      if (loaded.length) return loaded
      if (storeStages?.length) return storeStages.map((s) => ({ id: String(s.id), name: s.name }))
      return freeFlow ? DEMO_STAGES.map((s) => ({ id: s.id, name: s.name })) : []
   }, [fubStages, storeStages, freeFlow])

   const tags = useMemo(() => {
      const loaded =
         fubTags?.data?.filter((t) => t?.tagName?.trim()).map((t) => t.tagName) || []
      if (loaded.length) return loaded
      if (storeTags?.length) return storeTags
      return freeFlow ? DEMO_TAGS.map((t) => t.name) : []
   }, [fubTags, storeTags, freeFlow])

   const ponds: IPondLike[] = useMemo(() => {
      if (pondsData?.length) return pondsData
      if (storePonds?.length) return storePonds
      return freeFlow ? DEMO_PONDS : []
   }, [pondsData, storePonds, freeFlow])

   const recommended = useMemo(
      () =>
         ((recommendedData || []) as IRecommendedLike[]).length
            ? ((recommendedData || []) as IRecommendedLike[])
            : freeFlow
              ? [{ title: 'Zillow', source: 'Zillow' }]
              : [],
      [recommendedData, freeFlow]
   )

   const vocab: IVocabulary = useMemo(
      () => ({ sources: sources.map((s) => s.leadSource), stages, tags, ponds }),
      [sources, stages, tags, ponds]
   )

   const starters: IStarterCampaign[] = useMemo(
      () => buildStarterCampaigns({ recommended, sources, ponds }),
      [recommended, sources, ponds]
   )

   return {
      sources,
      stages,
      tags,
      ponds,
      recommended,
      vocab,
      starters,
      loading: sourcesLoading && sources.length === 0,
   }
}
